<template>
  <template v-if="isText">
    <span data-testid="text-column">{{ value }}</span>
  </template>
  <template v-if="isLink">
    <a
      v-if="hasValue && getLinkUrl()"
      :href="getLinkUrl()"
      :class="columnDataOptions.columnButtonClass"
      class="data-link"
      data-testid="link-column"
    >
      {{ getLinkText() }}
    </a>
    <span
      v-else-if="hasValue"
      data-testid="link-text-only"
    >
      {{ getLinkText() }}
    </span>
  </template>
  <template v-if="isModal">
    <span data-testid="modal-column">
      <modal-container 
        :modal-title="columnDataOptions.modalTitle"
        :button-text="columnDataOptions.modalButtonText"
        :button-type="columnDataOptions.modalButtonType"
        :button-icon="columnDataOptions.modalButtonIcon"
        :button-class="columnDataOptions.modalButtonClass"
        :show-delete="columnDataOptions.modalShowDelete"
      >
        <template #body>
          <component 
            :is="columnDataOptions.modalFormType" 
            :check-id="value" 
            :csrf-token="csrfToken"
          />
        </template>
        <template #footer-submit>
          <input 
            type="submit" 
            class="btn btn--primary-alt" 
            :form="columnDataOptions.modalFormType"
          >
        </template>
        <template #footer-delete>
          <a 
            :data-confirm="columnDataOptions.modalDataConfirmText"
            class="btn btn--delete"
            rel="nofollow"
            data-method="delete"
            :href="`/staffsafe/admin/checks/${value}`"
          >
            Delete Check
          </a>
        </template>
      </modal-container>
    </span>
  </template>
  <template v-if="isIcon">
    <svg-icon
      v-if="isIconSet(value)"
      :class="['data-table-card__icon', value?.css_class]"
      :style="{ fill: getIconColor() }"
      :icon="getIconName()"
      data-testid="icon-column"
    />
    <span
      v-else-if="hasIconText()"
      data-testid="icon-text"
      class="icon-text"
    >
      {{ getIconText() }}
    </span>
  </template>
  <template v-if="isActions">
    <div
      v-if="Array.isArray(value) && value.length > 0"
      data-testid="actions-column"
      class="actions-column d-flex align-items-center"
    >
      <a
        v-for="(action, index) in value"
        :key="index"
        :href="action.url"
        :class="action.class"
        v-bind="getDataAttributes(action.data)"
        class="action-link d-inline-flex align-items-center"
      >
        <svg-icon
          v-if="action.icon"
          :icon="action.icon"
          class="action-link__icon mr-1"
        />
        {{ action.text }}
      </a>
    </div>
  </template>
</template>

<script>
import { computed, defineComponent} from "vue";
import CheckForm  from "../check-form/check-form.vue";
export default defineComponent({
  name: "DataColumn",
  components:{
    CheckForm
  },
  props:{
    columnType:{
      type: String,
      required: true
    },
    value:{
      type: [String, Number, Object, null],
      required: true,
    },
    columnDataOptions:{
      type: Object,
      default: () => ({}),

    },
    csrfToken:{
      type: String,
      default: ""
    }
  },
  setup(props) {
    // Check if value is present (not null, undefined, or empty string)
    // Backend can return null for missing data or empty strings for empty fields
    const hasValue = computed(() => {
      return props.value != null && props.value !== "";
    });

    const isText = computed( ()=>{
      return props.columnType === "text";
    });

    const isLink = computed( ()=>{
      return props.columnType === "link";
    });

    const isModal = computed( ()=>{
      return props.columnType === "modal";
    });

    const isIcon = computed( ()=>{
      return props.columnType === "icon";
    });

    const isActions = computed( ()=>{
      return props.columnType === "actions";
    });

    // Helper to check if a value is a valid object (not null)
    const isValidObject = (val) => {
      return typeof val === "object" && val !== null;
    };

    const isIconSet = (val)=>{
      if (isValidObject(val)) {
        return val.icon != null && val.icon !== "";
      }
      return val != null && val !== "";
    };

    const getIconName = () => {
      // If value is an object with icon property, use that
      if (isValidObject(props.value)) {
        return props.value.icon;
      }
      // Otherwise treat value as the icon name (legacy behavior)
      return props.value;
    };

    const getIconColor = () => {
      // Priority 1: If value is an object with color property, use that
      if (isValidObject(props.value) && props.value.color) {
        return props.value.color;
      }
      // Priority 2: Use columnDataOptions.columnIconColor if provided (legacy behavior)
      if (props.columnDataOptions.columnIconColor) {
        return props.columnDataOptions.columnIconColor;
      }
      // Priority 3: Default to black
      return "#000000";
    };

    const getLinkUrl = () => {
      // If value is an object, return its url property (may be null)
      if (isValidObject(props.value)) {
        return props.value.url;
      }
      // Otherwise treat value as the URL (legacy behavior)
      return props.value;
    };

    const getLinkText = () => {
      // If value is an object with text property, use that
      if (isValidObject(props.value) && props.value.text) {
        return props.value.text;
      }
      // Otherwise use columnDataOptions.columnButtonText
      return props.columnDataOptions.columnButtonText;
    };

    const hasIconText = () => {
      // Check if value has a text property (for icon columns with text fallback like "n/a")
      if (isValidObject(props.value)) {
        return props.value.text != null && props.value.text !== "";
      }
      return false;
    };

    const getIconText = () => {
      // Return the text property from the value object
      if (isValidObject(props.value)) {
        return props.value.text;
      }
      return "";
    };

    const getDataAttributes = (data) => {
      // Convert data object to data-* attributes for v-bind
      if (!isValidObject(data)) return {};

      const attributes = {};
      for (const [key, value] of Object.entries(data)) {
        // Skip null/undefined to prevent "null"/"undefined" string literals in HTML
        if (value == null) continue;

        // Convert camelCase/snake_case to kebab-case for data attributes
        const kebabKey = key.replace(/([A-Z])/g, "-$1").replace(/_/g, "-").toLowerCase();
        attributes[`data-${kebabKey}`] = value;
      }
      return attributes;
    };

    return {hasValue, isText, isLink, isModal, isIcon, isActions, isIconSet, getIconName, getIconColor, getLinkUrl, getLinkText, hasIconText, getIconText, getDataAttributes};
  },
});
</script>

<style lang="scss" scoped>
.data-table-card__icon{
  width: 20px;
  height: 20px;
}

.data-link {
  color: #1e40af; // Primary blue color for links
  text-decoration: underline;
  font-size: 14px;
  font-weight: 400;

  &:hover {
    color: #1e3a8a; // Darker blue on hover
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.actions-column {
  gap: 8px;
}

.action-link {
  color: #1e40af; // Primary blue color for links
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    color: #1e3a8a; // Darker blue on hover
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.action-link__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  fill: #1e40af; // Match the link color
}

.action-link:hover .action-link__icon {
  fill: #1e3a8a; // Darker blue on hover to match link
}

// Secondary action styling (for Merge to)
.action-link--secondary {
  &:hover {
    color: #1f2937; // Darker gray on hover
    text-decoration: underline;
  }

  &:hover .action-link__icon {
    fill: #1f2937; // Darker gray on hover
  }
}

// Push the last action to the right if needed
.action-link.ml-auto {
  margin-left: auto;
}
</style>

